import omit from 'lodash-es/omit';
import type { RadarAutocompleteAddress } from 'radar-sdk-js/dist/types';
import { APO_FPO_OPTIONS, Alaska, Hawaii } from '../components/forms/address-options';
import type { OrderAddress } from '../bff-api/baskets/model';
import type { CustomerAddress } from '../bff-api/customers/model';
import type { DeliveryResponse } from '../bff-api/delivery/model';
import type { AutocompleteAddress } from '../types/radar';

const poBoxREs = [
    /PO Box/i,
    /P.O Box/i,
    /PO. Box/i,
    /P.O. Box/i,
    /POBox/i,
    /P.OBox/i,
    /PO.Box/i,
    /P.O.Box/i,
];

export const COUNTRY_CODE = 'US';
export const DEFAULT_STATE_CODE = 'NY';

export const EMPTY_ADDRESS: CustomerAddress = {
    addressId: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    countryCode: COUNTRY_CODE,
    preferred: false,
};
export const EMPTY_ORDER_ADDRESS: OrderAddress = {
    firstName: '',
    lastName: '',
    fullName: '',
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    countryCode: COUNTRY_CODE,
    c_email: '',
    c_signUpNewsletter: true,
};

export const isSameAddress = (
    a1?: OrderAddress | null,
    a2?: OrderAddress | null,
    checkAddressId = false,
): boolean => {
    if (a1 && a2) {
        if (checkAddressId && a1.addressId && a1.addressId === a2.addressId) {
            return true;
        }

        return ['address1', 'address2', 'city', 'stateCode', 'postalCode'].every(
            (field) => (!a1[field] && !a2[field]) || a1[field] === a2[field],
        );
    }

    // If just one is null, return false.
    return !a1 && !a2;
};

const ORDER_ADDRESS_FIELDS = [
    'firstName',
    'lastName',
    'address1',
    'address2',
    'city',
    'stateCode',
    'postalCode',
    'countryCode',
    'c_isGiftRegistryAddress',
    'c_isGiftRegistryShippingAddressChanged',
];
export const isSameOrderAddress = (
    a1: OrderAddress | undefined,
    a2: OrderAddress | undefined,
    checkContactFields = false,
): boolean => {
    if (a1 && a2) {
        return ORDER_ADDRESS_FIELDS.concat(checkContactFields ? ['phone', 'c_email'] : []).every(
            (field) => (!a1[field] && !a2[field]) || a1[field] === a2[field],
        );
    }

    // If just one is null, return false.
    return !a1 && !a2;
};

export const isSameOrderAddressForShippingMethods = (
    a1: OrderAddress | undefined,
    a2: OrderAddress | undefined,
): boolean => {
    if (a1 && a2) {
        const fields = ['city', 'stateCode', 'postalCode'];
        if (isPOBox(a2.address1)) {
            fields.push('address1');
        }
        return fields.every((field) => (!a1[field] && !a2[field]) || a1[field] === a2[field]);
    }

    // If just one is null, return false.
    return !a1 && !a2;
};

export const toOrderAddressDescription = (
    address?: OrderAddress,
    separator: string = ', ',
): string => {
    let description = '';
    if (address) {
        description += `${address.firstName ?? ''} ${address.lastName ?? ''}`.trim();

        if (description) {
            description += separator;
        }
    }

    return description + toAddressDescription(address, separator);
};

export const toAddressDescription = (address?: OrderAddress, separator: string = ', '): string => {
    let description = '';

    if (address) {
        if (address.address1) {
            description += `${address.address1}${separator}`;
        }
        if (address.address2) {
            description += `${address.address2}${separator}`;
        }
        let cityStateZip = '';
        if (address.city) {
            cityStateZip += `${address.city}, `;
        }
        if (address.stateCode) {
            cityStateZip += `${address.stateCode}, `;
        }
        if (address.postalCode) {
            cityStateZip += address.postalCode;
        }
        if (cityStateZip) {
            description += `${cityStateZip}`;
        }
    }

    if (description.endsWith(', ')) {
        description = description.substring(0, description.length - 2);
    }

    if (description.endsWith(separator)) {
        description = description.substring(0, description.length - separator.length);
    }

    return description;
};

export const isPOBox = (address?: string): boolean =>
    !!address && poBoxREs.some((poBox) => poBox.test(address));

export const isPOBoxAddress = ({ address1, address2 }: OrderAddress): boolean =>
    poBoxREs.some(
        (poBox) => (address1 && poBox.test(address1)) || (address2 && poBox.test(address2)),
    );

export const isAPOFPOAddress = (address?: OrderAddress | null): boolean => {
    if (address) {
        return APO_FPO_OPTIONS.some((opt) => opt.value === address.city);
    }

    return false;
};

export const isValidAddress = (
    address?: OrderAddress | null,
    options: { validatePoBox?: boolean; validateNames?: boolean } = {
        validatePoBox: true,
        validateNames: false,
    },
): boolean =>
    !!(
        address?.address1 &&
        address.city &&
        address.stateCode &&
        address.postalCode &&
        address.countryCode
    ) &&
    (!options.validatePoBox || !(isPOBox(address.address1) || isPOBox(address?.address2 ?? ''))) &&
    (!options.validateNames || !!(address?.firstName && address?.lastName));

export const isValidContactInfo = (address?: OrderAddress | null): boolean =>
    !!(address?.firstName && address?.lastName && address?.phone && address?.c_email);

export const toAddressInputValue = (
    address: RadarAutocompleteAddress | AutocompleteAddress | null,
): string => {
    if (!address) {
        return '';
    }

    let addressDescription = '';

    if (address.number) {
        addressDescription += `${address.number} `;
    }

    if (address.street) {
        addressDescription += `${address.street}, `;
    }

    if (address.borough) {
        addressDescription += `${address.borough}, `;
    }

    if (address.city) {
        addressDescription += `${address.city}, `;
    }

    if (address.stateCode) {
        addressDescription += address.stateCode;
    }

    if (addressDescription.endsWith(', ')) {
        addressDescription = addressDescription.slice(0, addressDescription.length - 2);
    }

    return addressDescription.trim();
};

export const toAddressSectionValue = (address: OrderAddress | undefined): string | undefined => {
    if (!address) {
        return undefined;
    }

    const stateAndPostal =
        address.stateCode && address.postalCode
            ? `${address.stateCode} ${address.postalCode}`
            : address.stateCode || address.postalCode;

    const addressParts = [address.address1, address.city, stateAndPostal].filter(Boolean);

    return `${addressParts.join(', ')}, United States`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toValueOrEmpty = (value?: any): string => {
    if (value) {
        return typeof value === 'string' ? value : `${value}`;
    }

    return '';
};

/**
 * Temporary solution (while we are migrating to bff-api) to remove
 * the fields that are not part of the address because we cannot
 * send them to the commerce-api.
 */
export const toCleanCustomerAddress = (address: CustomerAddress): CustomerAddress =>
    omit(address, [
        'id',
        'ID',
        'UUID',
        'useGiftRegistryAddress',
        'c_isGiftRegistryAddress',
        'listAddressID',
        'giftMessage1',
        'giftMessage2',
        'giftMessage3',
        'giftMessage4',
    ]) as CustomerAddress;

export const toOrderAddress = ({
    number,
    street,
    city,
    stateCode,
    countryCode,
    postalCode,
    latitude,
    longitude,
}: RadarAutocompleteAddress): OrderAddress => ({
    address1: `${number ? `${number} ` : ''}${street ?? ''}`,
    city,
    stateCode,
    countryCode: countryCode ?? COUNTRY_CODE,
    postalCode,
    c_latitude: latitude,
    c_longitude: longitude,
});

export const eddAddressToOrderAddress = ({
    city,
    stateCode,
    countryCode,
    postalCode,
}: DeliveryResponse): OrderAddress => ({
    city,
    stateCode,
    countryCode: countryCode ?? COUNTRY_CODE,
    postalCode,
});

export const customerAddressToOrderAddress = (
    address?: CustomerAddress | null,
): OrderAddress | undefined => {
    if (!address) {
        return undefined;
    }

    const {
        firstName,
        lastName,
        fullName,
        phone,
        address1,
        address2,
        city,
        stateCode,
        countryCode,
        postalCode,
    } = address;
    return {
        firstName,
        lastName,
        fullName,
        phone,
        address1,
        address2,
        city,
        stateCode,
        countryCode,
        postalCode,
    };
};

export const getFullName = ({ firstName = '', lastName = '' }: OrderAddress): string =>
    `${firstName} ${lastName}`.trim();

export const isHawaiiOrAlaskaState = (stateCode: string | undefined): boolean =>
    stateCode === Hawaii.value || stateCode === Alaska.value;
